/**
 * usePricingCalculatorForm
 *
 * Custom hook that encapsulates all business logic, state management, and calculations
 * for the Pricing Calculator Form. This hook handles form state, event handlers,
 * pricing calculations, and summary generation.
 *
 * @see src/modules/RetoolModule/components/PricingCalculatorForm.tsx - UI component
 * @see src/modules/RetoolModule/types/pricingCalculator.ts - Type definitions
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import {
  PricingCalculatorFormValues,
  PlanType,
  TenantType,
  isDedicatedTenant,
  PRICING_CONSTANTS,
  calculateSeatPricePerMonth,
  calculatePlatformFee,
  BASE_PLAN_FLAT_FEE_MAX_USERS,
  MOSAIC_ENTERPRISE_MIN_USERS,
  calculateOnboardingGroups,
  getOnboardingFeePerGroup,
  computePricingSummary,
} from '../types/pricingCalculator';

/**
 * Normalized inputs the rest of the defaults derive from. Every value here either
 * mirrors a runtime handler's normalization or feeds more than one returned field.
 */
type PricingBasis = {
  numberOfMembers: number;
  isItCustomer: boolean;
  planType: PlanType;
  tenantType: TenantType;
  directDatabaseIntegration: boolean;
  isolatedDatabaseDefault: boolean;
  pricePerMember: number;
  basePlatformFee: number;
  onboardingGroups: number;
};

const resolvePricingBasis = (
  providedInitialValues?: Partial<PricingCalculatorFormValues>,
): PricingBasis => {
  const numberOfMembers = providedInitialValues?.numberOfMembers || 10;
  const isItCustomer = providedInitialValues?.isItCustomer ?? false;
  const requestedPlanType = providedInitialValues?.planType || PlanType.STANDARD;
  // Mosaic Enterprise isn't available under 200 members; mirror the runtime
  // Enterprise→Standard fallback (handleNumberOfMembersChange) so initialValues
  // or URL params can't seed a self-inconsistent Enterprise-under-200 state.
  // Everything below (seat price, platform fee, onboarding, returned planType)
  // derives from this effective value. (GLX-1748, Copilot review.)
  const planType =
    requestedPlanType === PlanType.MOSAIC_ENTERPRISE &&
    numberOfMembers < MOSAIC_ENTERPRISE_MIN_USERS
      ? PlanType.STANDARD
      : requestedPlanType;

  // Mirror handleNumberOfMembersChange's auto-enable on initial render so a
  // shared quote link / initialValues that loads 100+ members without isolation
  // flags still gets Isolated DB on. Otherwise the tier dropdown would show the
  // disabled "Multi-Tenant" option and the quote would undercharge until the
  // member count is edited. (GLX-1751, Copilot review.)
  const tenantType = providedInitialValues?.tenantType ?? TenantType.SHARED;
  // Dedicated tenancy supersedes Direct Database — normalize so a URL with both
  // (e.g. tt=single & dd=1) can't seed a contradictory state that surfaces
  // Direct-DB-only controls and mis-disables the tier dropdown. Mirrors
  // handleTenantTypeChange. (GLX-1751, Copilot review.)
  const directDatabaseIntegration =
    !isDedicatedTenant(tenantType) && (providedInitialValues?.directDatabaseIntegration ?? false);
  const isolatedDatabaseDefault =
    numberOfMembers >= 100 && !isDedicatedTenant(tenantType) && !directDatabaseIntegration;

  // Base Plan has fixed pricing
  const isBasePlan = planType === PlanType.BASE;
  const pricePerMember = isBasePlan ? 9.99 : calculateSeatPricePerMonth(numberOfMembers, planType);

  const platformFeeData = calculatePlatformFee(numberOfMembers, planType);
  // Base ≤15 users: Fixed $999, no IT Customer discount
  const isBasePlanFlatFee = isBasePlan && numberOfMembers <= BASE_PLAN_FLAT_FEE_MAX_USERS;
  const basePlatformFee = isBasePlanFlatFee
    ? platformFeeData.platformFee
    : isItCustomer
      ? platformFeeData.platformFee / 2
      : platformFeeData.platformFee;

  // Onboarding fee = group count × per-group rate. Respect an explicit group override.
  const onboardingGroups =
    providedInitialValues?.numberOfOnboardingGroups || calculateOnboardingGroups(numberOfMembers);

  return {
    numberOfMembers,
    isItCustomer,
    planType,
    tenantType,
    directDatabaseIntegration,
    isolatedDatabaseDefault,
    pricePerMember,
    basePlatformFee,
    onboardingGroups,
  };
};

type SubscriptionDefaults = Pick<
  PricingCalculatorFormValues,
  | 'companyName'
  | 'subscriptionDuration'
  | 'isRenewal'
  | 'isItCustomer'
  | 'planType'
  | 'numberOfMembers'
  | 'pricePerMember'
  | 'discountOnMemberPrice'
  | 'numberOfInternalContractors'
  | 'numberOfGuests'
  | 'numberOfExternalContractors'
  | 'platformFee'
  | 'discountOnPlatformFee'
  | 'numberOfOnboardingGroups'
  | 'onboardingFee'
  | 'discountOnOnboardingFee'
  | 'additionalEnterpriseSupportHours'
>;

const buildSubscriptionDefaults = (
  providedInitialValues: Partial<PricingCalculatorFormValues> | undefined,
  {
    isItCustomer,
    planType,
    numberOfMembers,
    pricePerMember,
    basePlatformFee,
    onboardingGroups,
  }: PricingBasis,
): SubscriptionDefaults => ({
  companyName: providedInitialValues?.companyName || '',
  subscriptionDuration: providedInitialValues?.subscriptionDuration || 12,
  isRenewal: providedInitialValues?.isRenewal ?? false,
  isItCustomer,
  planType,
  numberOfMembers,
  pricePerMember: providedInitialValues?.pricePerMember || pricePerMember,
  discountOnMemberPrice: providedInitialValues?.discountOnMemberPrice || 0,
  numberOfInternalContractors: providedInitialValues?.numberOfInternalContractors || 0,
  numberOfGuests: providedInitialValues?.numberOfGuests || 0,
  numberOfExternalContractors: providedInitialValues?.numberOfExternalContractors || 0,
  platformFee: providedInitialValues?.platformFee || basePlatformFee,
  discountOnPlatformFee: providedInitialValues?.discountOnPlatformFee || 0,
  numberOfOnboardingGroups: onboardingGroups,
  onboardingFee:
    providedInitialValues?.onboardingFee || getOnboardingFeePerGroup(planType) * onboardingGroups,
  discountOnOnboardingFee: providedInitialValues?.discountOnOnboardingFee || 0,
  additionalEnterpriseSupportHours: providedInitialValues?.additionalEnterpriseSupportHours || 0,
});

type InfrastructureDefaults = Pick<
  PricingCalculatorFormValues,
  | 'directDatabaseIntegration'
  | 'discountOnDirectDatabase'
  | 'isolatedDatabase'
  | 'discountOnIsolatedDatabase'
  | 'isolatedDatabaseMigration'
  | 'tenantType'
  | 'discountOnTenant'
  | 'isolatedAi'
  | 'securityPackageSso'
  | 'discountOnSecurityPackageSso'
  | 'govCloud'
  | 'govCloudFee'
  | 'discountOnGovCloudFee'
>;

const buildInfrastructureDefaults = (
  providedInitialValues: Partial<PricingCalculatorFormValues> | undefined,
  { tenantType, directDatabaseIntegration, isolatedDatabaseDefault }: PricingBasis,
): InfrastructureDefaults => ({
  directDatabaseIntegration,
  discountOnDirectDatabase: providedInitialValues?.discountOnDirectDatabase || 0,
  // OR (not ??) so these auto-enables still win when a caller passes an
  // explicit `isolatedDatabase: false` (PricingCalculatorPage always does):
  // - Direct Database always includes an isolated DB (mirror
  //   handleDirectDatabaseChange on initial render), and
  // - 100+ shared (non-Direct-DB, non-dedicated) teams require isolation.
  // (Copilot review.)
  isolatedDatabase:
    directDatabaseIntegration ||
    (providedInitialValues?.isolatedDatabase ?? false) ||
    isolatedDatabaseDefault,
  discountOnIsolatedDatabase: providedInitialValues?.discountOnIsolatedDatabase || 0,
  isolatedDatabaseMigration: providedInitialValues?.isolatedDatabaseMigration ?? false,
  tenantType,
  discountOnTenant: providedInitialValues?.discountOnTenant || 0,
  isolatedAi: providedInitialValues?.isolatedAi ?? false,
  securityPackageSso: providedInitialValues?.securityPackageSso ?? false,
  discountOnSecurityPackageSso: providedInitialValues?.discountOnSecurityPackageSso || 0,
  govCloud: providedInitialValues?.govCloud ?? false,
  govCloudFee: providedInitialValues?.govCloudFee || 0,
  discountOnGovCloudFee: providedInitialValues?.discountOnGovCloudFee || 0,
});

type AddOnFeatureDefaults = Pick<
  PricingCalculatorFormValues,
  | 'aiForecasting'
  | 'discountOnAiForecasting'
  | 'nonPeopleResources'
  | 'discountOnNonPeopleResources'
  | 'profileBuilding'
  | 'discountOnProfileBuilding'
  | 'skillsTagging'
  | 'discountOnSkillsTagging'
>;

type SupportFeatureDefaults = Pick<
  PricingCalculatorFormValues,
  'solutionsSupport' | 'solutionsSupportHours' | 'enableWhiteLabel'
>;

type IntegrationFeatureDefaults = Pick<
  PricingCalculatorFormValues,
  | 'selectedIntegrations'
  | 'bakIntegrationFetchTimes'
  | 'enable4xDataTransfer'
  | 'integrationMigration'
  | 'discountOnIntegrationSetup'
  | 'discountOnIntegrationRecurring'
>;

type FeatureDefaults = AddOnFeatureDefaults & SupportFeatureDefaults & IntegrationFeatureDefaults;

const buildAddOnFeatureDefaults = (
  providedInitialValues: Partial<PricingCalculatorFormValues> | undefined,
): AddOnFeatureDefaults => ({
  aiForecasting: providedInitialValues?.aiForecasting ?? false,
  discountOnAiForecasting: providedInitialValues?.discountOnAiForecasting || 0,
  nonPeopleResources: providedInitialValues?.nonPeopleResources ?? false,
  discountOnNonPeopleResources: providedInitialValues?.discountOnNonPeopleResources || 0,
  profileBuilding: providedInitialValues?.profileBuilding ?? false,
  discountOnProfileBuilding: providedInitialValues?.discountOnProfileBuilding || 0,
  skillsTagging: providedInitialValues?.skillsTagging ?? false,
  discountOnSkillsTagging: providedInitialValues?.discountOnSkillsTagging || 0,
});

const buildSupportFeatureDefaults = (
  providedInitialValues: Partial<PricingCalculatorFormValues> | undefined,
): SupportFeatureDefaults => ({
  solutionsSupport: providedInitialValues?.solutionsSupport ?? false,
  solutionsSupportHours: providedInitialValues?.solutionsSupportHours || 5,
  enableWhiteLabel: providedInitialValues?.enableWhiteLabel ?? false,
});

const buildIntegrationFeatureDefaults = (
  providedInitialValues: Partial<PricingCalculatorFormValues> | undefined,
): IntegrationFeatureDefaults => ({
  selectedIntegrations: providedInitialValues?.selectedIntegrations || [],
  bakIntegrationFetchTimes:
    providedInitialValues?.bakIntegrationFetchTimes ||
    PRICING_CONSTANTS.DEFAULT_DATA_TRANSFER_FREQUENCY,
  enable4xDataTransfer: providedInitialValues?.enable4xDataTransfer ?? false,
  integrationMigration: providedInitialValues?.integrationMigration ?? false,
  discountOnIntegrationSetup: providedInitialValues?.discountOnIntegrationSetup || 0,
  discountOnIntegrationRecurring: providedInitialValues?.discountOnIntegrationRecurring || 0,
});

const buildFeatureDefaults = (
  providedInitialValues: Partial<PricingCalculatorFormValues> | undefined,
): FeatureDefaults => ({
  ...buildAddOnFeatureDefaults(providedInitialValues),
  ...buildSupportFeatureDefaults(providedInitialValues),
  ...buildIntegrationFeatureDefaults(providedInitialValues),
});

/**
 * Get initial form values with proper defaults and pricing calculations
 */
export const getInitialFormValues = (
  providedInitialValues?: Partial<PricingCalculatorFormValues>,
): PricingCalculatorFormValues => {
  const basis = resolvePricingBasis(providedInitialValues);

  return {
    ...buildSubscriptionDefaults(providedInitialValues, basis),
    ...buildInfrastructureDefaults(providedInitialValues, basis),
    ...buildFeatureDefaults(providedInitialValues),
  };
};

export interface UsePricingCalculatorFormProps {
  initialValues?: Partial<PricingCalculatorFormValues>;
  onSubmit?: (values: PricingCalculatorFormValues) => void;
}

export const usePricingCalculatorForm = ({
  initialValues: providedInitialValues,
  onSubmit,
}: UsePricingCalculatorFormProps = {}) => {
  const [values, setValues] = useState<PricingCalculatorFormValues>(() =>
    getInitialFormValues(providedInitialValues),
  );

  // Per-integration state management
  const [integrationMigrations, setIntegrationMigrations] = useState<Record<string, boolean>>({});
  const [integrationSetupDiscounts, setIntegrationSetupDiscounts] = useState<
    Record<string, number>
  >({});
  const [integrationRecurringDiscounts, setIntegrationRecurringDiscounts] = useState<
    Record<string, number>
  >({});
  const [integrationPipelineOpportunities, setIntegrationPipelineOpportunities] = useState<
    Record<string, number>
  >({});

  // Track the baseline integrations from a loaded renewal contract.
  // - `undefined` → no baseline contract was loaded; on renewal, setup is
  //   waived for everything (we can't tell what's genuinely new).
  // - `string[]` (including []) → baseline is known. Integrations not in
  //   this list are genuinely "newly added" and still get charged on
  //   renewal even if the baseline list itself was empty (GLX-1746).
  const [originalIntegrations] = useState<string[] | undefined>(
    providedInitialValues?.selectedIntegrations,
  );

  // Local state for the after-discount price input (to avoid buggy formatting during typing)
  // Calculate after-discount value (derived state)
  const calculatedAfterDiscount = useMemo(() => {
    return (values.pricePerMember * (1 - values.discountOnMemberPrice / 100)).toFixed(2);
  }, [values.pricePerMember, values.discountOnMemberPrice]);

  // User's input value (only set when user manually types)
  const [userAfterDiscountInput, setUserAfterDiscountInput] = useState<string | null>(null);

  // Use user input if available, otherwise use calculated value
  const afterDiscountInput = userAfterDiscountInput ?? calculatedAfterDiscount;

  // Ref to track if user is actively editing the after-discount field
  const isEditingAfterDiscountRef = useRef<boolean>(false);

  /**
   * Generic input change handler for simple form fields
   */
  const handleInputChange = useCallback(
    (field: keyof PricingCalculatorFormValues) => (event: React.ChangeEvent<HTMLInputElement>) => {
      let value: string | number | boolean =
        event.target.type === 'checkbox'
          ? event.target.checked
          : event.target.type === 'number'
            ? parseFloat(event.target.value) || 0
            : event.target.value;

      // Enforce minimum of 3 months for subscription duration
      if (field === 'subscriptionDuration' && typeof value === 'number') {
        value = Math.max(3, value);
      }

      setValues((prev) => {
        const updates: Partial<PricingCalculatorFormValues> = {
          [field]: value,
        };

        // Auto-calculate onboarding fee when number of groups changes
        if (field === 'numberOfOnboardingGroups' && typeof value === 'number') {
          updates.onboardingFee = value * getOnboardingFeePerGroup(prev.planType);
        }

        return {
          ...prev,
          ...updates,
        };
      });
    },
    [],
  );

  // Auto-calculate summary in real-time whenever any form value changes.
  // The pure computation lives in pricingCalculator.ts so unit tests can drive
  // it directly with fixture inputs (see pricingCalculatorValidationSet.test.ts).
  const summary = useMemo(
    () =>
      computePricingSummary(values, {
        originalIntegrations,
        integrationMigrations,
        integrationSetupDiscounts,
        integrationRecurringDiscounts,
        integrationPipelineOpportunities,
      }),
    [
      values,
      originalIntegrations,
      integrationMigrations,
      integrationSetupDiscounts,
      integrationRecurringDiscounts,
      integrationPipelineOpportunities,
    ],
  );

  /**
   * Handle form submission
   */
  const handleFormSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();

      if (onSubmit) {
        onSubmit(values);
      }
    },
    [values, onSubmit],
  );

  /**
   * Handle number of members change with special logic for Base
   * Also auto-enables isolated database for teams with >100 users
   * Auto-switches from Enterprise to Standard if members drop below 200
   */
  const handleNumberOfMembersChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newNumberOfMembers = parseInt(event.target.value) || 0;

    setValues((prev) => {
      // Calculate number of onboarding groups based on new member count
      const newOnboardingGroups = calculateOnboardingGroups(newNumberOfMembers);

      // Auto-enable isolated database for teams with 100+ users
      // (unless a dedicated tenant or Direct Database is already enabled, both of which include isolated DB)
      const shouldAutoEnableIsolatedDb =
        newNumberOfMembers >= 100 &&
        !isDedicatedTenant(prev.tenantType) &&
        !prev.directDatabaseIntegration;

      // Auto-switch from Enterprise to Standard if members drop below 200
      // Enterprise plan is not available for under 200 users
      const shouldSwitchFromEnterprise =
        prev.planType === PlanType.MOSAIC_ENTERPRISE &&
        newNumberOfMembers < MOSAIC_ENTERPRISE_MIN_USERS;
      const effectivePlanType = shouldSwitchFromEnterprise ? PlanType.STANDARD : prev.planType;

      // Auto-calculate onboarding fee: groups x plan-aware per-group rate
      // (Enterprise = $10k/group). Uses effectivePlanType so dropping below
      // 200 users (which forces Standard) also reverts the onboarding rate.
      const newOnboardingFee = newOnboardingGroups * getOnboardingFeePerGroup(effectivePlanType);

      // For all plans, calculate based on user count
      const newSeatPrice =
        effectivePlanType === PlanType.BASE
          ? 9.99
          : calculateSeatPricePerMonth(newNumberOfMembers, effectivePlanType);
      const platformFeeData = calculatePlatformFee(newNumberOfMembers, effectivePlanType);
      // Base ≤15 users: Fixed $999, no IT Customer discount
      const isBasePlanFlatFee =
        effectivePlanType === PlanType.BASE && newNumberOfMembers <= BASE_PLAN_FLAT_FEE_MAX_USERS;
      const newPlatformFee = isBasePlanFlatFee
        ? platformFeeData.platformFee
        : prev.isItCustomer
          ? platformFeeData.platformFee / 2
          : platformFeeData.platformFee;

      return {
        ...prev,
        numberOfMembers: newNumberOfMembers,
        planType: effectivePlanType,
        pricePerMember: newSeatPrice,
        platformFee: newPlatformFee,
        numberOfOnboardingGroups: newOnboardingGroups,
        onboardingFee: newOnboardingFee,
        // Auto-enable isolated database for large teams
        isolatedDatabase: shouldAutoEnableIsolatedDb ? true : prev.isolatedDatabase,
        // Reset Enterprise support hours when switching away from Enterprise
        additionalEnterpriseSupportHours: shouldSwitchFromEnterprise
          ? 0
          : prev.additionalEnterpriseSupportHours,
      };
    });
  }, []);

  /**
   * Handle plan type change with special logic for Base and Enterprise
   */
  const handlePlanTypeChange = useCallback(
    (planType: PlanType) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;

      setValues((prev) => {
        // Define base prices and multiplier for Enterprise Plan
        const basePrices = [9.99, 12.99, 14.99, 19.99, 24.99];
        const enterpriseMultiplier = 1.5;
        const enterprisePrices = basePrices.map(
          (price) => Math.round(price * enterpriseMultiplier * 100) / 100,
        );

        // Helper function to update price if it's a standard option
        const getUpdatedPrice = (
          currentPrice: number,
          oldPlanType: PlanType,
          newPlanType: PlanType,
        ) => {
          // Check if toggling Enterprise Plan
          const wasEnterprise = oldPlanType === PlanType.MOSAIC_ENTERPRISE;
          const isNowEnterprise = newPlanType === PlanType.MOSAIC_ENTERPRISE;

          // Only update if Enterprise status changed
          if (wasEnterprise === isNowEnterprise) {
            return currentPrice;
          }

          // Find if current price matches a standard option
          const oldPrices = wasEnterprise ? enterprisePrices : basePrices;
          const newPrices = isNowEnterprise ? enterprisePrices : basePrices;
          const priceIndex = oldPrices.findIndex((p) => Math.abs(p - currentPrice) < 0.01);

          // If current price is a standard option, update it
          if (priceIndex !== -1) {
            return newPrices[priceIndex];
          }

          // Otherwise, keep the current price (user might have custom value)
          return currentPrice;
        };

        // If unchecking, revert to STANDARD plan
        if (!isChecked) {
          const newSeatPrice = calculateSeatPricePerMonth(prev.numberOfMembers, PlanType.STANDARD);
          const platformFeeData = calculatePlatformFee(prev.numberOfMembers, PlanType.STANDARD);
          const basePlatformFee = prev.isItCustomer
            ? platformFeeData.platformFee / 2
            : platformFeeData.platformFee;

          // Update price if it's a standard option
          const updatedPrice = getUpdatedPrice(
            prev.pricePerMember,
            prev.planType,
            PlanType.STANDARD,
          );

          return {
            ...prev,
            planType: PlanType.STANDARD,
            pricePerMember: updatedPrice !== prev.pricePerMember ? updatedPrice : newSeatPrice,
            platformFee: basePlatformFee,
            // Reverting to Standard restores the $6k/group onboarding rate (GLX-1748).
            // Keep the existing group count so a user-edited value isn't clobbered.
            onboardingFee:
              getOnboardingFeePerGroup(PlanType.STANDARD) * prev.numberOfOnboardingGroups,
            additionalEnterpriseSupportHours: 0, // Reset Enterprise support hours
          };
        }

        // If checking, apply the selected plan
        // Calculate based on user count for all plans
        const newSeatPrice =
          planType === PlanType.BASE
            ? 9.99
            : calculateSeatPricePerMonth(prev.numberOfMembers, planType);
        const platformFeeData = calculatePlatformFee(prev.numberOfMembers, planType);
        // Base ≤15 users: Fixed $999, no IT Customer discount
        const isBasePlanFlatFee =
          planType === PlanType.BASE && prev.numberOfMembers <= BASE_PLAN_FLAT_FEE_MAX_USERS;
        const basePlatformFee = isBasePlanFlatFee
          ? platformFeeData.platformFee
          : prev.isItCustomer
            ? platformFeeData.platformFee / 2
            : platformFeeData.platformFee;

        // Update price if it's a standard option
        const updatedPrice = getUpdatedPrice(prev.pricePerMember, prev.planType, planType);

        return {
          ...prev,
          planType: planType,
          pricePerMember: updatedPrice !== prev.pricePerMember ? updatedPrice : newSeatPrice,
          platformFee: basePlatformFee,
          // Mosaic Enterprise Plan onboarding is $10k/group; others $6k/group (GLX-1748).
          // Keep the existing group count so a user-edited value isn't clobbered.
          onboardingFee: getOnboardingFeePerGroup(planType) * prev.numberOfOnboardingGroups,
          additionalEnterpriseSupportHours:
            planType === PlanType.MOSAIC_ENTERPRISE ? prev.additionalEnterpriseSupportHours : 0, // Base and Standard plans don't have enterprise support hours
        };
      });
    },
    [],
  );

  /**
   * Handle direct database integration change
   * - Automatically enables isolated database when direct database is enabled
   * - Forces tenancy back to SHARED when direct database is enabled (mutually exclusive with dedicated tenants)
   */
  const handleDirectDatabaseChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setValues((prev) => {
      const wasDedicated = isDedicatedTenant(prev.tenantType);
      return {
        ...prev,
        directDatabaseIntegration: checked,
        // Auto-check Isolated Database when Direct Database is enabled
        isolatedDatabase: checked ? true : prev.isolatedDatabase,
        // Force back to SHARED tenancy when Direct Database is enabled (mutually exclusive)
        tenantType: checked ? TenantType.SHARED : prev.tenantType,
        // When Direct Database is disabled, uncheck Isolated AI unless Isolated DB or dedicated tenant is still enabled
        isolatedAi: checked
          ? prev.isolatedAi
          : prev.isolatedDatabase || wasDedicated
            ? prev.isolatedAi
            : false,
      };
    });
  }, []);

  /**
   * Handle tenant type change (shared / single / enterprise)
   * - Switching to a dedicated tier (single or enterprise) clears Direct DB and the
   *   stand-alone Isolated DB (both are bundled into the dedicated tenant).
   * - Switching back to SHARED leaves Isolated AI on only if Isolated DB or Direct DB
   *   is still keeping the feature available.
   */
  const handleTenantTypeChange = useCallback((next: TenantType) => {
    setValues((prev) => {
      const becomingDedicated = isDedicatedTenant(next);
      return {
        ...prev,
        tenantType: next,
        directDatabaseIntegration: becomingDedicated ? false : prev.directDatabaseIntegration,
        isolatedDatabase: becomingDedicated ? false : prev.isolatedDatabase,
        isolatedAi: becomingDedicated
          ? prev.isolatedAi
          : prev.isolatedDatabase || prev.directDatabaseIntegration
            ? prev.isolatedAi
            : false,
      };
    });
  }, []);

  /**
   * Handle isolated database change
   * Unchecks Isolated AI when Isolated Database is unchecked
   * Note: Does NOT affect Single Tenant - user can freely toggle between Isolated Database and Single Tenant
   */
  const handleIsolatedDatabaseChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setValues((prev) => ({
      ...prev,
      isolatedDatabase: checked,
      isolatedAi: checked ? prev.isolatedAi : false, // Uncheck Isolated AI if Isolated Database is unchecked
    }));
  }, []);

  /**
   * Handle isolated AI change
   * Automatically enables Isolated Database when Isolated AI is enabled
   * (unless Single Tenant is already enabled, which includes Isolated Database)
   */
  const handleIsolatedAiChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setValues((prev) => ({
      ...prev,
      isolatedAi: checked,
      // Auto-check Isolated Database when Isolated AI is checked
      // (unless a dedicated tenant is selected — those already include Isolated Database)
      isolatedDatabase:
        checked && !isDedicatedTenant(prev.tenantType) ? true : prev.isolatedDatabase,
    }));
  }, []);

  /**
   * Handle IT Customer checkbox change with special logic for Base
   */
  const handleIsItCustomerChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    setValues((prev) => {
      // Base ≤15 users: Fixed $999, no IT Customer discount
      const isBasePlanFlatFee =
        prev.planType === PlanType.BASE && prev.numberOfMembers <= BASE_PLAN_FLAT_FEE_MAX_USERS;

      if (isBasePlanFlatFee) {
        return {
          ...prev,
          isItCustomer: checked,
          // Platform fee remains $999 for Base ≤15 users
        };
      }

      // For other plans (including Base 16+ users), apply IT Customer discount
      const platformFeeData = calculatePlatformFee(prev.numberOfMembers, prev.planType);

      return {
        ...prev,
        isItCustomer: checked,
        platformFee: checked ? platformFeeData.platformFee / 2 : platformFeeData.platformFee,
      };
    });
  }, []);

  /**
   * Handle integration selection change
   */
  const handleIntegrationsChange = useCallback((selected: string[]) => {
    setValues((prev) => ({
      ...prev,
      selectedIntegrations: selected,
    }));
  }, []);

  // Per-integration handlers
  const handleIntegrationMigrationChange = useCallback(
    (integrationKey: string, isMigration: boolean) => {
      setIntegrationMigrations((prev) => ({
        ...prev,
        [integrationKey]: isMigration,
      }));
    },
    [],
  );

  const handleIntegrationDiscountChange = useCallback(
    (integrationKey: string, field: 'setup' | 'recurring', value: number) => {
      // Only update per-integration discount state, NOT the global form values
      // The global discountOnIntegrationSetup/discountOnIntegrationRecurring are for
      // different purposes (legacy global discount) and shouldn't be overwritten
      if (field === 'setup') {
        setIntegrationSetupDiscounts((prev) => ({
          ...prev,
          [integrationKey]: value,
        }));
      } else {
        setIntegrationRecurringDiscounts((prev) => ({
          ...prev,
          [integrationKey]: value,
        }));
      }
    },
    [],
  );

  const handleIntegrationPipelineOpportunitiesChange = useCallback(
    (integrationKey: string, count: number) => {
      setIntegrationPipelineOpportunities((prev) => ({
        ...prev,
        [integrationKey]: count,
      }));
    },
    [],
  );

  return {
    // State
    values,
    setValues,
    summary,

    // Per-integration state
    integrationMigrations,
    integrationSetupDiscounts,
    integrationRecurringDiscounts,
    integrationPipelineOpportunities,
    originalIntegrations,

    // After-discount input state (for UI)
    afterDiscountInput,
    setUserAfterDiscountInput,
    isEditingAfterDiscountRef,

    // Event Handlers
    handleInputChange,
    handleNumberOfMembersChange,
    handlePlanTypeChange,
    handleDirectDatabaseChange,
    handleTenantTypeChange,
    handleIsolatedDatabaseChange,
    handleIsolatedAiChange,
    handleIsItCustomerChange,
    handleIntegrationsChange,
    handleFormSubmit,

    // Per-integration handlers
    handleIntegrationMigrationChange,
    handleIntegrationDiscountChange,
    handleIntegrationPipelineOpportunitiesChange,
  };
};
